import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import type { FieldPath, UseFormReturn } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { DateTime } from 'luxon';
import {
  Box,
  Dialog,
  DialogContent,
  LinearProgress,
  styled,
} from '@mui/material';

import { usePortalContext } from '@/contexts/PortalProvider';
import { usePortalLabel } from '@/hooks/usePortalLabel';

import { useAppDispatch } from '@/hooks/useAppDispatch';

import { AppDispatch } from '@/data/store';
import { ReservationFormSteps } from './schema/reservationFormSteps.model';
import { Passenger } from '@/data/models/passenger.model';
import { TripLegType } from '@/data/models/reservation.model';
import { ServicePlan } from '@/data/models/service-plan.model';
import type { TransportationAttribute } from '@/data/models/transportation-attribute.model';
import {
  ReservationFormInput,
  MEMBER_FORM_PHONE_FIELDS,
  reservationFormSchema,
  buildEmptyReservationFormValues,
  type ReservationFormValues,
} from './schema/reservationForm.schema';

import reservationUtils from '@/services/reservationUtils';
import { triggerReservationPhoneFieldValidation } from './utils/triggerReservationPhoneFieldValidation';
import {
  isLowConfidenceError,
  tripLegConfidenceErrorTranslationKey,
} from './machines/tripLegConfidence';
import type { BundledValidationOutput } from './machines/runBundledValidation';

import * as lookupSlice from '@/data/lookup.slice';

import {
  ReservationFormMachinesProvider,
  useReservationFormMachines,
} from './ReservationFormMachinesContext';
import { ReservationFormDebug } from './hooks/useDebugReservationMachines';

import ReservationDialogHeader from '@/components/home/reservations/ReservationDialogHeader';
import { ReservationTripLegForm } from './ReservationTripLegForm';
import { ReservationMemberForm } from './ReservationMemberForm';
import { ReservationSummary } from './ReservationSummary';
import RequestOverrideDialog from './prompts/RequestOverrideDialog';
import ConfirmationDialog from '../ConfirmationDialog';

const PREFIX = 'CreateReservationDialog';

const classes = {
  content: `${PREFIX}-content`,
  pages: `${PREFIX}-pages`,
};

/** Paths from `getReservationFormFieldErrors`, e.g. `trips[1].pickupDateTime`. */
function tripLegIndexFromFormFieldPath(field: string): number | undefined {
  const m = /^trips\[(\d+)\]\./.exec(field);
  if (m == null) return undefined;
  const n = Number(m[1]);
  return Number.isFinite(n) ? n : undefined;
}

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.content}`]: {
    padding: 0,
    overflow: 'visible',
  },
  [`& .${classes.pages}`]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(3),
    maxWidth: '1152px',
    margin: '0 auto',
    [theme.breakpoints.up('md')]: {
      paddingLeft: theme.spacing(10),
      paddingRight: theme.spacing(10),
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(3),
    },
    [theme.breakpoints.up('lg')]: {
      paddingLeft: theme.spacing(18),
      paddingRight: theme.spacing(18),
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(3),
    },
  },
}));

function ReservationFormSubmit({
  form,
  onSubmit,
}: {
  form: UseFormReturn<ReservationFormInput, unknown, ReservationFormValues>;
  onSubmit: (
    values: ReservationFormValues,
    options: { saveAsDraft: boolean },
  ) => Promise<void>;
}) {
  const { mainSnapshot } = useReservationFormMachines();

  const wasSuccessRef = useRef(false);
  useEffect(() => {
    if (!mainSnapshot.matches('success')) {
      wasSuccessRef.current = false;
      return;
    }
    if (wasSuccessRef.current) return;
    wasSuccessRef.current = true;
    if (onSubmit) {
      onSubmit(form.getValues() as ReservationFormValues, {
        saveAsDraft: Boolean(mainSnapshot.context.saveAsDraft),
      });
    }
  }, [mainSnapshot, onSubmit, form]);

  return null;
}

function ReservationFormSubmitProgress() {
  const { mainSnapshot } = useReservationFormMachines();
  const isSubmitting =
    mainSnapshot.matches('submitting') || mainSnapshot.matches('validating');

  if (!isSubmitting) return null;

  return <LinearProgress />;
}

interface CreateReservationDialogProps {
  open: boolean;
  onClose: () => void;
  passenger: Passenger | null;
  servicePlans: ServicePlan[];
  transportationAttributes?: TransportationAttribute[];
}

const CreateReservationDialog = ({
  open,
  onClose,
  passenger,
  servicePlans,
  transportationAttributes,
}: CreateReservationDialogProps) => {
  const { t } = useTranslation('common');
  const dispatch: AppDispatch = useAppDispatch();
  const [activeStep, setActiveStep] = useState(0);
  const activeStepRef = useRef(activeStep);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const { isMemberPortal } = usePortalContext();
  // Robust portal check (subdomain-classified). Client portal downgrades
  // low address-confidence errors to warnings, so they're not applied as red
  // field errors here.
  const isClientPortal = usePortalLabel() === 'client-portal';

  // Check the runtimeConfig setting for state machine debugging
  const { __environment__ } = window as any;
  const stateMachineDebugEnabled = Boolean(
    __environment__?.REACT_APP_STATE_MACHINE_DEBUG,
  );

  const form = useForm<ReservationFormInput, unknown, ReservationFormValues>({
    resolver: zodResolver(reservationFormSchema),
    defaultValues: buildEmptyReservationFormValues(
      passenger,
      transportationAttributes,
    ),
    mode: 'onBlur',
  });
  const { isDirty } = form.formState;
  const pickUpTime = form.watch('trips.0.pickupDateTime');
  const appointmentDate = form.watch('trips.0.appointmentDate');

  useLayoutEffect(() => {
    activeStepRef.current = activeStep;
  }, [activeStep]);

  useEffect(() => {
    if (!open || activeStep !== ReservationFormSteps.MEMBER) return;
    triggerReservationPhoneFieldValidation(form);
  }, [open, activeStep, form]);

  useEffect(() => {
    if (!open) return;
    form.reset(
      buildEmptyReservationFormValues(passenger, transportationAttributes),
    );
    triggerReservationPhoneFieldValidation(form);
  }, [open, passenger?.passengerUUID, transportationAttributes]);

  const applyTripLegValidationRef = useRef<
    (output: BundledValidationOutput) => void
  >(() => {});
  /**
   * Field paths this callback set as server errors on the prior `/validate`
   * round. Diff-cleared each round so we only wipe the paths we actually set,
   * leaving RHF/Zod errors (e.g. `trips.X.reasonForTrip` required) intact.
   * Mirrors `prevValidationFieldsRef` in the legacy admin-portal
   * `home/reservations/ReservationDialog.tsx` (~L280-296).
   */
  const appliedServerErrorPathsRef = useRef<FieldPath<ReservationFormInput>[]>(
    [],
  );

  /**
   * Validation endpoint results are applied to the form here.
   */
  useEffect(() => {
    applyTripLegValidationRef.current = (output: BundledValidationOutput) => {
      const { validations, errorsByTripIndex } = output;
      // Update the form values returned from validation results. Apparently only timezones
      for (const update of validations.tripLegUpdates) {
        const idx = update.tripLegSequence - 1;
        if (update.pickUpAddress?.timeZone) {
          if (form.getValues('trips')[idx] == null) continue;
          if (
            form.getValues('trips')[idx].pickupTimeZone !==
            update.pickUpAddress.timeZone
          ) {
            form.setValue(
              `trips.${idx}.pickupTimeZone`,
              update.pickUpAddress.timeZone,
              { shouldValidate: true },
            );
          }
        }
        if (update.dropOffAddress?.timeZone) {
          if (form.getValues('trips')[idx] == null) continue;
          if (
            form.getValues('trips')[idx].dropoffTimeZone !==
            update.dropOffAddress.timeZone
          ) {
            form.setValue(
              `trips.${idx}.dropoffTimeZone`,
              update.dropOffAddress.timeZone,
              { shouldValidate: true },
            );
          }
        }
      }
      // Diff-clear: drop only the paths we set on the prior pass. Single
      // batched call matches legacy's `formHook.clearErrors(removedFields)`.
      const priorAppliedPaths = appliedServerErrorPathsRef.current;
      if (priorAppliedPaths.length > 0) {
        form.clearErrors(priorAppliedPaths);
      }
      // One combined message list: /validate field errors first, then /score
      // trip-leg errors appended last so a trip-leg error wins if both target
      // the same field path. Reading `errorsByTripIndex` from the
      // fresh `output` (not machine context) is required: this runs before the
      // context assign, so context would still hold the prior round's errors.
      const tripType = form.getValues('tripType');
      // Client portal shows low address-confidence (/validate's
      // LOW_CONFIDENCE_ADDRESS) as a yellow warning under the field
      // (TripArrayFieldAddressInput), not a red blocking error — so exclude it
      // from the /validate errors applied here. Other portals keep it as a red
      // field error.
      const redValidateErrors = isClientPortal
        ? validations.errors.filter((e) => !isLowConfidenceError(e))
        : validations.errors;
      const validateMessages =
        redValidateErrors.length > 0
          ? reservationUtils.getReservationFormFieldErrors(
              t,
              tripType === TripLegType.RoundTrip,
              { ...validations, errors: redValidateErrors },
            )
          : [];

      const tripLegMessages: { field: string; message: string }[] = [];
      for (const err of Object.values(errorsByTripIndex)) {
        // Target field is resolved once at score time (resolveConfidenceErrorFieldPath).
        if (!err.formFieldPath) continue;
        const translationKey = tripLegConfidenceErrorTranslationKey(err);
        const message =
          (translationKey
            ? (t(translationKey, {
                ...(err.errorCodeData ?? {}),
                defaultValue: err.message ?? '',
              }) as string)
            : err.message) || '';
        if (!message) continue;
        tripLegMessages.push({ field: err.formFieldPath, message });
      }

      const messages = [...validateMessages, ...tripLegMessages];

      const nextAppliedPaths: FieldPath<ReservationFormInput>[] = [];
      if (messages.length > 0) {
        const tripsSnapshot = form.getValues('trips');
        const genericErrors: string[] = [];

        for (const message of messages) {
          if (message.field === 'GenericError') {
            genericErrors.push(message.message);
            continue;
          }

          const legIndex = tripLegIndexFromFormFieldPath(message.field);
          if (legIndex != null && tripsSnapshot[legIndex] == null) {
            continue;
          }

          const fieldPath = message.field as FieldPath<ReservationFormInput>;
          if (form.getValues(fieldPath) == null) continue;

          form.setError(fieldPath, {
            type: 'manual',
            message: message.message,
          });
          nextAppliedPaths.push(fieldPath);
        }

        if (genericErrors.length > 0) {
          form.setError('genericErrors', {
            type: 'manual',
            message: Array.from(new Set(genericErrors)).join('\n'),
          });
          nextAppliedPaths.push(
            'genericErrors' as FieldPath<ReservationFormInput>,
          );
        }
      } else if (priorAppliedPaths.length > 0) {
        // Both endpoints cleared their errors this round but had some last round
        // — re-run the resolver so any client-side (Zod) errors on the freed
        // paths get re-surfaced. Mirrors the legacy
        // `formHook.trigger(undefined, { shouldFocus: false })` at
        // home/reservations/ReservationDialog.tsx:267-270.
        form.trigger(undefined, { shouldFocus: false });
      }
      appliedServerErrorPathsRef.current = nextAppliedPaths;
      const isViewingMemberForm =
        activeStepRef.current === ReservationFormSteps.MEMBER;
      triggerReservationPhoneFieldValidation(
        form,
        !isViewingMemberForm
          ? { fieldsToExclude: MEMBER_FORM_PHONE_FIELDS }
          : undefined,
      );
    };
  }, [form, isClientPortal, t]);

  const tripLegServiceDate =
    (pickUpTime instanceof DateTime && pickUpTime.isValid
      ? pickUpTime.toISODate()
      : null) ??
    (appointmentDate instanceof DateTime && appointmentDate.isValid
      ? appointmentDate.toISODate()
      : null) ??
    (typeof appointmentDate === 'string' && appointmentDate.trim()
      ? appointmentDate
      : null) ??
    undefined;
  const overridableServicePlans = servicePlans.filter(
    ({ effectiveStartDate, effectiveEndDate }) =>
      tripLegServiceDate != null &&
      effectiveStartDate <= tripLegServiceDate &&
      (!effectiveEndDate || tripLegServiceDate <= effectiveEndDate),
  );

  const handleStep = (step: number) => () => {
    setActiveStep(step);
  };

  const handleReservationSubmit = useCallback(
    async (
      _values: ReservationFormValues,
      _options: { saveAsDraft: boolean },
    ) => {
      form.reset(
        buildEmptyReservationFormValues(passenger, transportationAttributes),
      );
      setActiveStep(0);
      onClose();
    },
    [form, onClose, passenger, transportationAttributes],
  );

  const handlePrevious = (evt: any) => {
    evt.preventDefault();
    setActiveStep(activeStep - 1);
  };

  const handleNext = (evt: any) => {
    evt.preventDefault();
    setActiveStep(activeStep + 1);
  };

  const handleCloseDialog = () => {
    if (isDirty) {
      setConfirmDialogOpen(true);
    } else {
      onClose();
    }
  };

  useEffect(() => {
    // Fetch all Create Reservation dialog data that isn't passed via props
    dispatch(lookupSlice.fetchTransportationAttributeMapAsync());
    dispatch(lookupSlice.fetchLookupItemsAsync('timeZone'));
    dispatch(lookupSlice.fetchLookupItemsAsync('tripLegPassengerNoteType'));
    dispatch(lookupSlice.fetchLookupItemsAsync('tripLegDriverNoteType'));
  }, []);

  return (
    <StyledDialog open={open} onClose={onClose} fullScreen>
      <ReservationDialogHeader
        passengerName={
          isMemberPortal
            ? undefined
            : `${passenger?.firstName} ${passenger?.lastName}`
        }
        title={
          isMemberPortal ? t('member.bookATrip') : t('reservation.createTrip')
        }
        steps={[
          t('reservation.tripDetailsStep'),
          t('reservation.member'),
          t('reservation.summary'),
        ]}
        activeStep={activeStep}
        isValid={form.formState.isValid}
        step={handleStep}
        open={handleCloseDialog}
      />
      <ReservationFormMachinesProvider
        clientBC={passenger?.clientBC ?? ''}
        passengerUUID={passenger?.passengerUUID ?? ''}
        passengerPhone={
          passenger ? reservationUtils.getPassengerPhone(passenger) : undefined
        }
        applyTripLegValidationResultsToForm={() =>
          applyTripLegValidationRef.current
        }
        getPickupTimeZoneForTrip={(tripIndex) =>
          form.getValues('trips')[tripIndex]?.pickupTimeZone || undefined
        }>
        <ReservationFormSubmitProgress />
        <DialogContent className={classes.content}>
          <Box className={classes.pages}>
            {stateMachineDebugEnabled && <ReservationFormDebug form={form} />}
            <ReservationFormSubmit
              form={form}
              onSubmit={handleReservationSubmit}
            />
            <RequestOverrideDialog
              servicePlans={overridableServicePlans}
              reservationForm={form}
            />
            {activeStep === 0 && (
              <ReservationTripLegForm
                form={form}
                onNext={handleNext}
                servicePlans={servicePlans}
              />
            )}
            {activeStep === 1 && (
              <ReservationMemberForm
                form={form}
                transportationAttributes={transportationAttributes ?? []}
                onNext={handleNext}
                onPrevious={handlePrevious}
              />
            )}
            {activeStep === 2 && (
              <ReservationSummary form={form} onPrevious={handlePrevious} />
            )}
          </Box>
        </DialogContent>
      </ReservationFormMachinesProvider>
      <ConfirmationDialog
        title={t('common.discardChanges')}
        message={t('common.possibleDataLoss')}
        open={confirmDialogOpen}
        positiveAction={onClose}
        negativeAction={() => setConfirmDialogOpen(false)}
      />
    </StyledDialog>
  );
};

export { CreateReservationDialog };
